"use client";

import type { Icon, IconProps } from "@phosphor-icons/react";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { CopyIcon } from "@phosphor-icons/react/dist/csr/Copy";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/csr/PencilSimple";
import { cva, type VariantProps } from "class-variance-authority";
import { Button } from "design-system/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "design-system/dialog";
import Link from "next/link";
import type { CSSProperties, MouseEventHandler, ReactNode } from "react";
import { toast } from "sonner";
import { LoadValue } from "@/components/custom/load-value";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/custom/tooltip";
import { getChainConfig, getChainIconUrl } from "@/config/chains/helpers";
import type { ChainIdentifier } from "@/config/chains/types";
import { BankIcon } from "@/config/icons";
import type { TokenIdentifier } from "@/config/tokens/types";
import { useDialog } from "@/lib/hooks/use-dialog";
import { useSafeCopyToClipboard } from "@/lib/hooks/use-safe-copy-to-clipboard";
import { cn } from "@/lib/utils";
import { type DispatchAgoraEventProps, dispatchAgoraEvent } from "@/lib/utils/dispatch-agora-event";
import { allValuesExist } from "@/lib/utils/is-true";
import { applyMask } from "@/lib/utils/mask";

const cardVariants = cva("flex w-full flex-col rounded-lg border overflow-hidden", {
  defaultVariants: {
    theme: "default",
  },
  variants: {
    theme: {
      default: "border-neutral-200 bg-background",
      info: "bg-blue-50 border-blue-100",
      success: "bg-[#ECFDF5] border-[#ECFDF5]",
      "very-important-warning": "bg-red-100 border-red-200",
      warning: "bg-amber-50 border-amber-200",
    },
  },
});

export interface CardProps extends VariantProps<typeof cardVariants> {
  children?: ReactNode;
  className?: string;
  shadow?: boolean;
  style?: CSSProperties | undefined;
}

export function Card({ children, className, shadow, style, theme = "default" }: CardProps) {
  return (
    <div className={cn(cardVariants({ theme }), className, shadow && "shadow-lg")} style={style}>
      {children}
    </div>
  );
}

const spacingVariants = {
  comfy: "p-8",
  default: "p-5",
  none: "p-0",
  snug: "p-4",
  tight: "p-2",
};

Card.Content = function Content({
  children,
  className,
  copy,
  copyClassName,
  spacing = "default",
  title,
  titleClassName,
}: {
  children?: ReactNode;
  className?: string;
  copy?: ReactNode;
  copyClassName?: string;
  spacing?: "comfy" | "default" | "none" | "snug" | "tight";
  title?: ReactNode;
  titleClassName?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-2", spacingVariants[spacing], className)}>
      {title && <h3 className={cn("ausd-heading-large text-foreground", copyClassName)}>{title}</h3>}

      {copy && <p className={cn("ausd-text-base text-neutral-600", titleClassName)}>{copy}</p>}

      {children}
    </div>
  );
};

Card.MaxWidthWrapper = function MaxWidthWrapper({ children, className }: { children?: ReactNode; className?: string }) {
  return <div className={cn("mx-auto w-full max-w-[720px]", className)}>{children}</div>;
};

Card.Footer = function Footer({ children, className }: { children?: ReactNode; className?: string }) {
  return <div className={cn("w-full border-t border-neutral-100 bg-neutral-100 py-2", className)}>{children}</div>;
};

export function CopyButton({
  buttonClassName,
  copyValue,
  disableTooltip,
  eventLocation = "body",
  label,
  labelClassName,
  tense = "was",
  text,
}: {
  buttonClassName?: string;
  copyValue: null | string;
  disableTooltip?: boolean;
  eventLocation?: "body" | "modal";
  label?: string;
  labelClassName?: string;
  tense?: "was" | "were";
  text?: null | string;
}) {
  const [_, safeCopy] = useSafeCopyToClipboard({
    onSuccess: () => {
      dispatchAgoraEvent({
        eventType: "product.clicked",
        eventAction: "interact",
        eventLocation,
        elementText,
      });
      toast("Copied!", {
        description: (
          <>
            <span className={labelClassName}>{label && label.charAt(0).toUpperCase() + label.slice(1)}</span> {tense}{" "}
            just added to your clipboard.
          </>
        ),
      });
    },
    onError: (err) => {
      console.error("Failed to copy: ", err);
      toast.error("Could not copy to clipboard.");
    },
  });

  if (!copyValue) {
    return null;
  }

  const elementText = label ? `Copy ${label}` : "Copy";

  const content = (
    <Button
      aria-label={elementText}
      className={cn("-my-9 shrink-0 grow-0", buttonClassName)}
      onClick={async (e) => {
        e.stopPropagation(); // This prevents the click from reaching the td in the table-view
        await safeCopy(copyValue);
      }}
      size={text ? "default" : "icon"}
      variant="ghost"
    >
      <CopyIcon />
      <span className="sr-only">
        Copy <span className={labelClassName}>{label}</span>
      </span>

      {text && <span className="ausd-text-sm">{text}</span>}
    </Button>
  );

  if (disableTooltip) {
    return content;
  }

  return (
    <TooltipProvider>
      <Tooltip
        eventProps={{
          elementKey: "copy-button-tooltip",
        }}
      >
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right">
          <p className="ausd-text-sm">
            Copy <span className={labelClassName}>{label}</span>
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

Card.Divider = function CardDivider({ className, isNested }: { className?: string; isNested?: boolean }) {
  return <hr className={cn("h-[1px] border-none bg-neutral-200", isNested && "mx-5", className)} />;
};

Card.Eyebrow = function CardEyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "ausd-text-base-semibold mb-2 flex w-full flex-row items-center justify-start gap-2 text-neutral-800 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:grow-0",
        className,
      )}
    >
      {children}
    </div>
  );
};

Card.TaskContainer = function TaskContainer({
  children,
  leftIcon: LeftIcon,
  leftIconProps,
  rightIcon: RightIcon,
  rightIconProps,
  theme = "task",
}: {
  children: ReactNode;
  leftIcon: Icon | null | undefined;
  leftIconProps?: IconProps;
  rightIcon: Icon | null | undefined;
  rightIconProps?: IconProps;
  theme?: CardTaskTheme;
}) {
  return (
    <div className="flex flex-row items-center justify-between px-5 py-1">
      {LeftIcon && (
        <div
          className={cn(
            "flex size-8 shrink-0 grow-0 items-center justify-center rounded-lg p-1 md:h-16 md:w-16 md:p-3",
            theme === "task" && "bg-stone-100 group-hover:bg-stone-200",
            theme === "action" && "bg-ausd-green group-hover:bg-ausd-green/90",
          )}
        >
          <LeftIcon
            className={cn(
              "size-5 md:h-10 md:w-10",
              theme === "task" && "text-stone-500",
              theme === "action" && "text-white",
            )}
            {...leftIconProps}
          />
        </div>
      )}

      {children}

      {RightIcon && (
        <div className="flex size-8 shrink-0 grow-0 items-center justify-center rounded-lg p-1 md:h-16 md:w-16 md:p-3">
          <RightIcon className="text-uasd-green fill-ausd-green size-5 md:h-7 md:w-7" {...rightIconProps} />
        </div>
      )}
    </div>
  );
};

Card.TasksWrapper = function TasksWrapper({ children }: { children?: ReactNode }) {
  return <div className="flex flex-col gap-5">{children}</div>;
};

type CardTaskTheme = "action" | "task";

Card.Task = function Task({
  children,
  copy,
  eventDetails,
  href,
  leftIcon,
  leftIconProps,
  onClick,
  rightIcon,
  rightIconProps,
  taskComplete,
  theme = "task",
  title,
}: {
  children?: ReactNode;
  copy?: ReactNode;
  /** Additional event details/dimensions for call-to-action tracking */
  eventDetails?: DispatchAgoraEventProps["eventDetails"];
  href?: string;
  leftIcon?: Icon | null | undefined;
  leftIconProps?: IconProps;
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  rightIcon?: Icon | null | undefined;
  rightIconProps?: IconProps;
  taskComplete?: boolean;
  theme?: CardTaskTheme;
  title?: ReactNode;
}) {
  function handleDispatchEvent() {
    // Only dispatch if the task is actionable (has href or onClick) and not complete
    if ((href || onClick) && !taskComplete) {
      dispatchAgoraEvent({
        eventAction: "call-to-action",
        eventType: "product.clicked",
        elementText: typeof title === "string" ? title : undefined,
        elementHref: href,
        eventLocation: "body",
        eventDetails,
      });
    }
  }

  const content = (
    <Card
      className={cn(
        "w-full text-left",
        (href || onClick) && !taskComplete && "group cursor-pointer",
        taskComplete && "cursor-not-allowed opacity-60",
        theme === "task" && "hover:bg-neutral-50",
        theme === "action" && "border-0 bg-neutral-50 hover:bg-neutral-100",
      )}
    >
      <Card.TaskContainer
        leftIcon={leftIcon}
        leftIconProps={leftIconProps}
        rightIcon={taskComplete ? CheckIcon : rightIcon}
        rightIconProps={rightIconProps}
        theme={theme}
      >
        <Card.Content className="shrink grow text-balance" copy={copy} title={title} />
      </Card.TaskContainer>

      {children}
    </Card>
  );

  if (href) {
    return (
      <Link href={href} onClick={handleDispatchEvent}>
        {content}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button
        onClick={(evt) => {
          handleDispatchEvent();
          onClick(evt);
        }}
        type="button"
      >
        {content}
      </button>
    );
  }

  return content;
};

Card.Page = function Page({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-6">{children}</div>;
};

Card.PageSection = function PageSection({ children }: { children: ReactNode }) {
  return <div className="flex flex-col">{children}</div>;
};

Card.TwoCardLayout = function TwoColLayout({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">{children}</div>;
};

Card.StackedLayout = function StackedLayout({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-4">{children}</div>;
};

type CellBlockTextMaskVariant = "maskMiddle" | "onlyShowLast4" | null | undefined;

type CellBlockTextTypeVariant = "address" | null | undefined;

Card.CellBlockText = function CellBlockText({
  className,
  iconName,
  isPrivate,
  maskVariant,
  text,
  typeVariant,
}: {
  className?: string;
  iconName?: null | string | undefined;
  isPrivate?: boolean;
  maskVariant?: CellBlockTextMaskVariant;
  text?: null | string | undefined;
  typeVariant?: CellBlockTextTypeVariant;
}) {
  let renderedText = text;

  if (renderedText && maskVariant) {
    renderedText = applyMask(renderedText, maskVariant);
  }

  if (!renderedText && iconName === "bank") {
    renderedText = "Bank Account";
  }

  const content = (
    <div className={cn(typeVariant === "address" && "font-mono font-medium break-all", className)}>{renderedText}</div>
  );

  if (isPrivate) {
    return (
      <div className="usprivacy" data-private>
        {content}
      </div>
    );
  }

  return content;
};

Card.CellBlock = function CellBlock({
  copyValue,
  editForm,
  iconDetails,
  label,
  primaryText,
  primaryTextClassName,
  primaryTextIsHighlighted,
  primaryTextIsPrivate,
  primaryTextMask,
  primaryTextType,
  secondaryText,
  secondaryTextClassName,
  secondaryTextIsPrivate,
  secondaryTextMask,
  secondaryTextType,
  showValueWhenAllExist,
  tertiaryText,
  tertiaryTextClassName,
  tertiaryTextIsPrivate,
  tertiaryTextMask,
  tertiaryTextType,
}: {
  copyValue?: string;
  editForm?: ReactNode;
  iconDetails?:
    | {
        identifier: string;
        type: "other";
      }
    | {
        identifier?: null | undefined;
        type: unknown;
      }
    | ChainIdentifier
    | TokenIdentifier;
  label?: string;
  primaryText?: string;
  primaryTextClassName?: string;
  primaryTextIsHighlighted?: boolean;
  primaryTextIsPrivate?: boolean;
  primaryTextMask?: CellBlockTextMaskVariant;
  primaryTextType?: CellBlockTextTypeVariant;
  secondaryText?: string;
  secondaryTextClassName?: string;
  secondaryTextIsPrivate?: boolean;
  secondaryTextMask?: CellBlockTextMaskVariant;
  secondaryTextType?: CellBlockTextTypeVariant;
  showValueWhenAllExist?: unknown[];
  tertiaryText?: string;
  tertiaryTextClassName?: string;
  tertiaryTextIsPrivate?: boolean;
  tertiaryTextMask?: CellBlockTextMaskVariant;
  tertiaryTextType?: CellBlockTextTypeVariant;
}) {
  let IconNode: React.ReactNode = null;

  if (iconDetails?.type === "token" && typeof iconDetails.identifier === "string") {
    IconNode = (
      <img
        alt={iconDetails.identifier}
        className="h-full max-h-full w-full max-w-full"
        onError={(e) => {
          e.currentTarget.src = `/${iconDetails.type}-icons/fallback.svg`;
        }}
        src={`/token-icons/${iconDetails.identifier.toLowerCase().replaceAll(" ", "-")}.svg`}
      />
    );
  } else if (iconDetails?.type === "chain" && typeof iconDetails.identifier === "number") {
    IconNode = (
      <img
        alt={getChainConfig(iconDetails.identifier)?.name}
        className="h-full max-h-full w-full max-w-full"
        onError={(e) => {
          e.currentTarget.src = `/${iconDetails.type}-icons/fallback.svg`;
        }}
        src={getChainIconUrl(iconDetails.identifier)}
      />
    );
  } else if (iconDetails?.type === "other") {
    // Currently this is our only other icon type, but we can add more in the future and
    // differentiate based on the identifier passed in.
    IconNode = <BankIcon />;
  }

  const hasLeftOrRightIcons = Boolean(IconNode) || Boolean(copyValue) || Boolean(editForm);

  let blockContent = (
    <div className="flex flex-col gap-1">
      {primaryText && (
        <Card.CellBlockText
          className={cn(
            "ausd-text-base-medium",
            primaryTextIsHighlighted && "rounded-sm bg-neutral-100 px-2 py-1",
            primaryTextClassName,
          )}
          iconName={
            iconDetails?.type === "other" && typeof iconDetails.identifier === "string"
              ? iconDetails.identifier
              : undefined
          }
          isPrivate={primaryTextIsPrivate}
          maskVariant={primaryTextMask}
          text={primaryText}
          typeVariant={primaryTextType}
        />
      )}

      {secondaryText && (
        <Card.CellBlockText
          className={cn("ausd-text-base text-neutral-600", secondaryTextClassName)}
          iconName={
            iconDetails?.type === "other" && typeof iconDetails.identifier === "string"
              ? iconDetails.identifier
              : undefined
          }
          isPrivate={secondaryTextIsPrivate}
          maskVariant={secondaryTextMask}
          text={secondaryText}
          typeVariant={secondaryTextType}
        />
      )}

      {tertiaryText && (
        <Card.CellBlockText
          className={cn("ausd-text-base text-neutral-600", tertiaryTextClassName)}
          iconName={
            iconDetails?.type === "other" && typeof iconDetails.identifier === "string"
              ? iconDetails.identifier
              : undefined
          }
          isPrivate={tertiaryTextIsPrivate}
          maskVariant={tertiaryTextMask}
          text={tertiaryText}
          typeVariant={tertiaryTextType}
        />
      )}
    </div>
  );

  let showValue = true;

  if (showValueWhenAllExist) {
    showValue = allValuesExist(showValueWhenAllExist);

    // add loading component wrapper if necessary
    blockContent = <LoadValue showValue={showValue} value={blockContent} />;
  }

  return (
    <div className="flex flex-col gap-2">
      {label && <label className="ausd-text-base text-neutral-600">{label}</label>}

      <div className={cn(hasLeftOrRightIcons && "flex flex-row items-start justify-start")}>
        {IconNode && (
          <div className="mt-[2px] mr-2 size-4 shrink-0 grow-0">
            <div className="h-full max-h-full w-full max-w-full overflow-hidden rounded-full">{IconNode}</div>
          </div>
        )}

        {blockContent}

        {copyValue && (
          <div
            className={cn(
              "mt-[1px] ml-1 h-4 w-9 shrink-0 grow-0 overflow-visible",
              primaryTextIsHighlighted && "translate-y-1",
            )}
          >
            <CopyButton copyValue={copyValue} label={label} />
          </div>
        )}

        {editForm && (
          <div className={cn("mt-[1px] ml-1 h-4 w-9 shrink-0 grow-0", primaryTextIsHighlighted && "translate-y-1")}>
            <EditButton editForm={editForm} label={label} />
          </div>
        )}
      </div>
    </div>
  );
};

export function EditButton({
  buttonClassName,
  editForm = null,
  label,
}: {
  buttonClassName?: string;
  editForm?: null | ReactNode;
  label?: string;
}) {
  const dialogControls = useDialog({
    eventProps: {
      elementKey: "edit-button",
      eventDetails: {
        label,
      },
    },
  });
  const desc = `Edit ${label}`;

  return (
    <>
      <TooltipProvider>
        <Tooltip
          eventProps={{
            elementKey: "edit-button-tooltip",
          }}
        >
          <TooltipTrigger asChild>
            <Button
              aria-label={desc}
              className={cn("-my-9 shrink-0 grow-0", buttonClassName)}
              data-testid={`edit-${label?.toLowerCase().replace(/\s+/g, "-")}-btn`}
              onClick={(e) => {
                e.preventDefault();

                dialogControls?.trigger();
              }}
              size="icon"
              variant="ghost"
            >
              <PencilSimpleIcon />
              <span className="sr-only">{desc}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p className="ausd-text-sm">{desc}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog {...dialogControls.props}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{desc}</DialogTitle>
            <DialogDescription>Make changes here. Click submit when you&apos;re done.</DialogDescription>
          </DialogHeader>

          {editForm}
        </DialogContent>
      </Dialog>
    </>
  );
}
